import { Navigate, Outlet, useParams } from 'react-router-dom';

import { useGetBrands } from '../../api/generated/insights';
import RouteGuardLoading from './RouteGuardLoading';

/**
 * Route guard that validates brandId parameter exists in the user's brand list.
 * Redirects to root if brandId is invalid or doesn't exist.
 * Renders nested routes via Outlet.
 */
export const BrandIdGuard: React.FC = () => {
  const { brandId } = useParams<{ brandId: string }>();

  const {
    data: brandsResponse,
    isLoading,
    error,
    isFetched,
  } = useGetBrands({
    query: {
      select: ({ data }) => data,
    },
  });

  const brands = brandsResponse?.brands || [];

  // Show loading state while fetching brands or if data hasn't been fetched yet
  if (isLoading || !isFetched) {
    return <RouteGuardLoading />;
  }

  // If there's an error fetching brands, redirect to root
  if (error) {
    // console.error('Error fetching brands for validation:', error);
    return <Navigate to="/" replace />;
  }

  // If brandId is missing or invalid, redirect to root
  if (!brandId || brandId.trim() === '') {
    return <Navigate to="/" replace />;
  }

  // Check if brandId exists in the user's brand list
  const brandExists =
    brands.length > 0 &&
    brands.some((brand) => String(brand.id) === brandId);

  // Only redirect if we have fetched data and the brand doesn't exist
  if (isFetched && !brandExists) {
    return <Navigate to="/" replace />;
  }

  // brandId is valid, render children
  return <Outlet />;
};

export default BrandIdGuard;
